import React, { useEffect, useLayoutEffect, useRef } from 'react';

import { BASELINE_COLOR, RX1_PALETTE, RX2_PALETTE, THRESHOLD_COLORS } from '@ui/Components/Sdr/canvasRendering';
import {
    AlertMarker,
    BaselinesPayload,
    CanvasMetricsSet,
    CursorState,
    RunningStats,
    SweepDrawConfig,
    SweepRows,
    ThresholdColors,
    TracePalette,
} from '@ui/Types/Gnss';
import { customToast } from '@ui/Utils/toasts';

import {
    AXIS_LABEL_FONT,
    AXIS_TICK_EPSILON,
    getAxisLabelStepMHz,
    getAxisMinorStepMHz,
    isAxisStepAligned,
} from '../math/axisLabels';
import {
    formatSweepFrequencyLabel,
    getMaximumFiniteValue,
    getPreferredSweepTraceMode,
    getSweepCursorFrequency,
    getSweepFrequencyDisplayUnit,
    getSweepSampleAtBin,
    getSweepTraceValueAtBin,
} from '../math/frequency';
import {
    getSweepContentWidth,
    SPECTRUM_WATERFALL_AXIS_HEIGHT_PX,
    SPECTRUM_WATERFALL_LABEL_AREA_PX,
} from '../math/layout';
import {
    getAdaptiveWaterfallColor,
    resolveAdaptiveWaterfallScale,
    smoothAdaptiveWaterfallScale,
    type WaterfallScaleRange,
} from '../math/waterfallColorScale';

export interface UseSweepRendererParams {
    // Optional alert markers to visualize detected anomalies on the spectrum
    alertMarkersRef?: React.RefObject<AlertMarker[]>;
    axisCanvasRef: React.RefObject<HTMLCanvasElement | null>;
    baselineChannel?: 'rx1' | 'rx2';
    baselineColor?: string;
    // Optional full baseline payload with channel selection
    baselineRef?: React.RefObject<BaselinesPayload | null> | null;
    // Optional independent baseline rows; last row is used for overlays
    baselineRowsRef?: React.RefObject<SweepRows> | null;
    canvasMetricsRef: React.RefObject<CanvasMetricsSet>;
    captureRecentTile?: () => Promise<void> | void;
    colorOffsetDb: number;
    containerRef: React.RefObject<HTMLDivElement | null>;
    contentBoxRef: React.RefObject<HTMLDivElement | null>;
    // Optional cursor ref to draw crosshair and data marker at hovered frequency
    cursorRef?: React.RefObject<CursorState | null>;
    devicePixelRatio: number;
    drawConfigRef?: React.RefObject<SweepDrawConfig | null>;
    fillWaterfallViewport?: boolean;
    frameCounterRef: React.RefObject<number>;
    height: number;
    highThresholdPct?: number; // percentage (0-100)
    // Key that changes when layout toggles (e.g., display mode) to trigger re-measure
    layoutKey?: unknown;
    leftPadPx: number;
    maxRows: number;
    mediumThresholdPct?: number; // percentage (0-100)
    // (legend removed) color legend is shown outside canvases in the UI
    // Stroke color palette for primary traces (latest/avg/peak)
    palette?: TracePalette;
    pxPerMHz: number;
    rightPadPx?: number;
    rowHeightPx: number;
    // Optional secondary channel rows for combined single-canvas overlay
    secondaryRowsRef?: React.RefObject<SweepRows> | null;
    secondaryStatsRef?: React.RefObject<RunningStats | null>;
    // Optional overlays: draw threshold lines offset from baseline (latest) by dB amounts
    showAlertThresholds?: boolean;
    showAverage: boolean;
    // Optional: draw the baseline curve itself
    showBaseline?: boolean;
    showLatest: boolean;
    showPeak: boolean;
    // Snapshot tile height threshold (px) to trigger capture on accumulated rows
    snapshotTileHeightPx?: number;
    spectrumCanvasRef: React.RefObject<HTMLCanvasElement | null>;
    spectrumCssH: number;
    // Optional running stats (avg/peak) for primary and secondary channels
    statsRef?: React.RefObject<RunningStats | null>;
    sweepRowsRef?: React.RefObject<SweepRows> | null;
    thresholdColors?: ThresholdColors;
    totalMHz: number;
    waterfallCanvasRef: React.RefObject<HTMLCanvasElement | null>;
    // Optional waterfall CSS height override (used by combined-stacked)
    waterfallCssHeightOverride?: number;
}

export default function useSweepRenderer(params: UseSweepRendererParams) {
    // Per-renderer alert freeze state: once cap is reached, keep drawing the first N oldest.
    // Matches the admin package's alertMarkerBuffer dedup cap (MAX_ALERT_MARKERS_PER_CHANNEL);
    // hardcoded here since useSweepRenderer lives in a separate Yarn workspace package.
    const ALERTS_DRAW_CAP = 4096;
    const alertFreezeRef = useRef<{
        frozen: AlertMarker[] | null;
        notified: boolean;
    }>({ frozen: null, notified: false });
    // Persist snapshot accumulation across effect re-renders so UI changes (zoom/fit/toggles)
    // do not reset counters and cause unintended captures.
    const accumPxSinceCaptureRef = useRef<number>(0);
    const lastRowsLenRef = useRef<number>(0);
    const pxPerMHzRef = useRef<number>(params.pxPerMHz);
    const smoothedWaterfallScaleRef = useRef<WaterfallScaleRange | null>(null);
    const lastWaterfallScaleUpdateAtMsRef = useRef<number>(0);
    const drawAxisRef = useRef<(() => void) | null>(null);
    const drawSpectrumRef = useRef<(() => void) | null>(null);
    const drawWaterfallRef = useRef<(() => void) | null>(null);
    const measureRef = useRef<(() => void) | null>(null);
    const {
        alertMarkersRef,
        axisCanvasRef,
        baselineChannel = 'rx1',
        baselineColor,
        baselineRef,
        baselineRowsRef,
        canvasMetricsRef,
        captureRecentTile,
        colorOffsetDb,
        containerRef,
        contentBoxRef,
        cursorRef,
        devicePixelRatio,
        drawConfigRef,
        fillWaterfallViewport = false,
        frameCounterRef,
        height,
        highThresholdPct,
        layoutKey,
        leftPadPx,
        maxRows,
        mediumThresholdPct,
        palette,
        pxPerMHz,
        rightPadPx = 0,
        rowHeightPx,
        secondaryRowsRef,
        secondaryStatsRef,
        showAlertThresholds,
        showAverage,
        showBaseline,
        showLatest,
        showPeak,
        snapshotTileHeightPx,
        spectrumCanvasRef,
        spectrumCssH,
        statsRef,
        sweepRowsRef,
        thresholdColors,
        totalMHz,
        waterfallCanvasRef,
        waterfallCssHeightOverride,
    } = params;

    useEffect(() => {
        const labelAreaPx = SPECTRUM_WATERFALL_LABEL_AREA_PX;
        // Internal draw functions (capture the latest refs/state via closure)
        const drawAxis = () => {
            const currentPxPerMHz = pxPerMHzRef.current;
            const axisCanvas = axisCanvasRef.current;
            const container = containerRef.current;
            if (!axisCanvas || !container) {
                return;
            }
            const cssWidth = container.clientWidth;
            const cssHeight = SPECTRUM_WATERFALL_AXIS_HEIGHT_PX;
            axisCanvas.style.width = cssWidth.toString() + 'px';
            axisCanvas.style.height = cssHeight.toString() + 'px';
            const maxDevicePixels = 8192;
            const desiredW = Math.floor(cssWidth * devicePixelRatio);
            const desiredH = Math.floor(cssHeight * devicePixelRatio);
            axisCanvas.width = Math.min(desiredW, maxDevicePixels);
            axisCanvas.height = desiredH;
            const ctx = axisCanvas.getContext('2d');
            if (!ctx) {
                return;
            }
            const xScaleAxis = axisCanvas.width / cssWidth;
            const yScaleAxis = axisCanvas.height / cssHeight;
            ctx.setTransform(xScaleAxis, 0, 0, yScaleAxis, 0, 0);
            canvasMetricsRef.current = {
                axis: { cssH: cssHeight, cssW: cssWidth, devH: axisCanvas.height, devW: axisCanvas.width },
                spectrum: canvasMetricsRef.current.spectrum,
                waterfall: canvasMetricsRef.current.waterfall,
            };
            ctx.clearRect(0, 0, cssWidth, cssHeight);
            ctx.fillStyle = '#111';
            ctx.fillRect(0, 0, cssWidth, cssHeight);
            ctx.strokeStyle = '#e0d200';
            ctx.fillStyle = '#e0d200';
            ctx.lineWidth = 1;
            const viewLeft = container.scrollLeft;
            const viewRight = container.scrollLeft + Math.max(0, container.clientWidth - 1);
            const effectiveLeftPad = leftPadPx + labelAreaPx;
            const fVisibleStart = Math.max(0, (viewLeft - effectiveLeftPad) / Math.max(1e-6, currentPxPerMHz));
            const fVisibleEnd = Math.min(totalMHz, (viewRight - effectiveLeftPad) / Math.max(1e-6, currentPxPerMHz));
            ctx.font = AXIS_LABEL_FONT;
            const labelStepMHz = getAxisLabelStepMHz({
                ctx,
                pxPerMHz: currentPxPerMHz,
                totalMHz,
                visibleEndMHz: fVisibleEnd,
                visibleStartMHz: fVisibleStart,
            });
            const axisUnit = getSweepFrequencyDisplayUnit({
                stepMHz: labelStepMHz,
                visibleEndMHz: fVisibleEnd,
                visibleStartMHz: fVisibleStart,
            });
            const minorStepMHz = getAxisMinorStepMHz(labelStepMHz, currentPxPerMHz);

            ctx.fillStyle = '#9c952f';
            ctx.font = '10px monospace';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
            ctx.fillText(axisUnit.label, 6, 2);

            if (minorStepMHz) {
                ctx.strokeStyle = '#9b9120';
                ctx.lineWidth = 1;

                const startMinorIndex = Math.max(0, Math.floor(fVisibleStart / minorStepMHz) - 1);
                const endMinorIndex = Math.ceil(fVisibleEnd / minorStepMHz) + 1;

                for (let tickIndex = startMinorIndex; tickIndex <= endMinorIndex; tickIndex += 1) {
                    const frequencyMHz = tickIndex * minorStepMHz;

                    if (frequencyMHz < -AXIS_TICK_EPSILON || frequencyMHz > totalMHz + AXIS_TICK_EPSILON) {
                        continue;
                    }

                    if (isAxisStepAligned(frequencyMHz, labelStepMHz)) {
                        continue;
                    }

                    const x = Math.floor(effectiveLeftPad + frequencyMHz * currentPxPerMHz - viewLeft);

                    if (x < 0 || x > cssWidth - 1) {
                        continue;
                    }

                    ctx.beginPath();
                    ctx.moveTo(x + 0.5, cssHeight);
                    ctx.lineTo(x + 0.5, cssHeight - 7);
                    ctx.stroke();
                }
            }

            ctx.strokeStyle = '#e0d200';
            ctx.fillStyle = '#f3e768';
            ctx.font = AXIS_LABEL_FONT;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';

            const startLabelIndex = Math.max(0, Math.floor(fVisibleStart / labelStepMHz) - 1);
            const endLabelIndex = Math.ceil(fVisibleEnd / labelStepMHz) + 1;
            let lastLabelRightPx = -Infinity;

            for (let tickIndex = startLabelIndex; tickIndex <= endLabelIndex; tickIndex += 1) {
                const frequencyMHz = tickIndex * labelStepMHz;

                if (frequencyMHz < -AXIS_TICK_EPSILON || frequencyMHz > totalMHz + AXIS_TICK_EPSILON) {
                    continue;
                }

                const x = Math.floor(effectiveLeftPad + frequencyMHz * currentPxPerMHz - viewLeft);

                if (x < 0 || x > cssWidth - 1) {
                    continue;
                }

                ctx.beginPath();
                ctx.moveTo(x + 0.5, cssHeight);
                ctx.lineTo(x + 0.5, cssHeight - 18);
                ctx.stroke();

                const label = formatSweepFrequencyLabel({
                    frequencyMHz,
                    stepMHz: labelStepMHz,
                    unit: axisUnit,
                });
                const labelWidthPx = ctx.measureText(label).width;
                const labelLeftPx = x - labelWidthPx / 2;
                const labelRightPx = x + labelWidthPx / 2;

                if (labelLeftPx < 2 || labelRightPx > cssWidth - 2 || labelLeftPx < lastLabelRightPx + 8) {
                    continue;
                }

                ctx.fillText(label, x, 2);
                lastLabelRightPx = labelRightPx;
            }

            ctx.strokeStyle = '#222';
            ctx.beginPath();
            ctx.moveTo(0, cssHeight - 0.5);
            ctx.lineTo(cssWidth, cssHeight - 0.5);
            ctx.stroke();
        };

        // Resolves the set of alert markers to draw, applying the shared freeze-at-cap policy so
        // drawSpectrum and drawWaterfall never disagree about which markers are frozen.
        const resolveAlertMarkersToDraw = (): AlertMarker[] => {
            if (!alertMarkersRef?.current || !Array.isArray(alertMarkersRef.current)) {
                return [];
            }
            // If upstream cleared markers (e.g., Reset Canvases), unfreeze the cap state
            if (alertMarkersRef.current.length === 0 && alertFreezeRef.current.frozen) {
                alertFreezeRef.current.frozen = null;
                alertFreezeRef.current.notified = false;
            }
            if (alertFreezeRef.current.frozen) {
                return alertFreezeRef.current.frozen;
            }
            const all = alertMarkersRef.current;
            if (all.length >= ALERTS_DRAW_CAP) {
                alertFreezeRef.current.frozen = all.slice(0, ALERTS_DRAW_CAP);
                if (!alertFreezeRef.current.notified) {
                    alertFreezeRef.current.notified = true;
                    try {
                        customToast({
                            message: `Alert draw limit reached (${ALERTS_DRAW_CAP}). New alerts will not render in this view.`,
                            options: { id: 'alerts-cap-reached' },
                        });
                    } catch {
                        // ignore toast errors
                    }
                }
                return alertFreezeRef.current.frozen;
            }
            return all.slice(0, Math.min(all.length, ALERTS_DRAW_CAP));
        };

        const drawSpectrum = () => {
            const currentPxPerMHz = pxPerMHzRef.current;
            const canvas = spectrumCanvasRef.current;
            const container = containerRef.current;
            if (!canvas || !container) {
                return;
            }
            const viewportCssW = container.clientWidth;
            const cssHeight = spectrumCssH;
            canvas.style.width = viewportCssW.toString() + 'px';
            canvas.style.height = cssHeight.toString() + 'px';
            const viewLeft = container.scrollLeft;
            canvas.style.left = viewLeft.toString() + 'px';
            const maxDevicePixels = 16_384;
            const desiredW = Math.floor(viewportCssW * devicePixelRatio);
            const desiredH = Math.floor(cssHeight * devicePixelRatio);
            canvas.width = Math.min(desiredW, maxDevicePixels);
            canvas.height = Math.min(desiredH, maxDevicePixels);
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return;
            }
            const xScaleSpec = canvas.width / viewportCssW;
            const yScaleSpec = canvas.height / cssHeight;
            ctx.setTransform(xScaleSpec, 0, 0, yScaleSpec, 0, 0);
            canvasMetricsRef.current = {
                axis: canvasMetricsRef.current.axis,
                spectrum: { cssH: cssHeight, cssW: viewportCssW, devH: canvas.height, devW: canvas.width },
                waterfall: canvasMetricsRef.current.waterfall,
            };
            ctx.fillStyle = '#0a0f1f';
            ctx.fillRect(0, 0, viewportCssW, cssHeight);
            ctx.strokeStyle = '#23304d';
            ctx.lineWidth = 1;

            // Map dB -> y coordinate. Keep this here so grid labels match trace mapping.
            const toY = (db: number) => {
                const clamped = Math.max(-120, Math.min(0, db));
                const t = (clamped + 120) / 120;
                return cssHeight - Math.floor(t * (cssHeight - 10)) - 2;
            };

            const effectiveLeftPad = leftPadPx + labelAreaPx;

            // Draw horizontal grid lines every 5 dB (0, -5, -10, ... -120)
            ctx.fillStyle = '#cfd8dc';
            const fontSize = Math.min(10, Math.max(8, Math.floor(8 + (devicePixelRatio || 1))));
            ctx.font = String(fontSize) + 'px monospace';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            for (let db = 0; db >= -120; db -= 5) {
                const y = toY(db);
                if (y < 0 || y > cssHeight) {
                    continue;
                }
                // Slightly emphasize every 10 dB
                if (db % 10 === 0) {
                    ctx.strokeStyle = '#35506a';
                } else {
                    ctx.strokeStyle = '#23304d';
                }
                ctx.beginPath();
                // draw grid lines across the full width so they continue behind the label column
                ctx.moveTo(0, y);
                ctx.lineTo(viewportCssW, y);
                ctx.stroke();

                // Draw label only every 10 dB (no ' dB' suffix per request)
                if (db % 10 === 0) {
                    ctx.fillStyle = '#cfd8dc';
                    ctx.fillText(db.toString(), 4, y);
                }
            }
            const rows = sweepRowsRef?.current ? sweepRowsRef.current : [];
            const rows2 = secondaryRowsRef ? secondaryRowsRef.current : undefined;
            const baselineRows = baselineRowsRef ? baselineRowsRef.current : undefined;
            const baselinePayload = baselineRef ? baselineRef.current : null;
            const latest = rows.at(-1) ?? [];
            let baselineLatest: number[] = [];
            if (baselineRows && baselineRows.length > 0) {
                baselineLatest = baselineRows.at(-1) ?? [];
            } else if (baselinePayload) {
                const ch = baselineChannel === 'rx2' ? baselinePayload.rx2 : baselinePayload.rx1;
                if (Array.isArray(ch) && ch.length > 0) {
                    baselineLatest = Array.from(ch);
                }
            }
            const fStart =
                drawConfigRef?.current && typeof drawConfigRef.current.freqStartMHz === 'number'
                    ? drawConfigRef.current.freqStartMHz
                    : 0;
            const binWidth =
                drawConfigRef?.current && typeof drawConfigRef.current.binWidthMHz === 'number'
                    ? drawConfigRef.current.binWidthMHz
                    : 1;
            const maxLen = Math.max(latest.length, baselineLatest.length);
            if (maxLen === 0) {
                return;
            }
            const viewRight = viewLeft + viewportCssW;
            const fVisibleStart = Math.max(0, (viewLeft - effectiveLeftPad) / Math.max(1e-6, currentPxPerMHz));
            const fVisibleEnd = Math.min(totalMHz, (viewRight - effectiveLeftPad) / Math.max(1e-6, currentPxPerMHz));
            const iStart = Math.max(0, Math.floor((fVisibleStart - fStart) / Math.max(1e-6, binWidth)));
            const iEnd = Math.min(maxLen - 1, Math.ceil((fVisibleEnd - fStart) / Math.max(1e-6, binWidth)));
            const xScaleSpecLocal = canvas.width / viewportCssW;
            const devicePxPerBin = binWidth * currentPxPerMHz * xScaleSpecLocal * devicePixelRatio;
            const binsPerColumn = devicePxPerBin < 1 ? Math.max(1, Math.floor(1 / Math.max(1e-6, devicePxPerBin))) : 1;
            const rowCount = Math.min(rows.length, maxRows);
            const startRowIndex = Math.max(0, rows.length - rowCount);
            // Prefer running stats if provided; fallback to windowed computation
            let avg: number[] = latest.length > 0 ? new Array<number>(latest.length).fill(0) : [];
            let peak: number[] = latest.length > 0 ? new Array<number>(latest.length).fill(-120) : [];
            const runStats = statsRef?.current ? statsRef.current : null;
            if (runStats && runStats.avg.length > 0) {
                const end = Math.min(latest.length, runStats.avg.length);
                avg = Array.from(runStats.avg.slice(0, end));
                peak = Array.from(runStats.peak.slice(0, end));
            } else if (latest.length > 0) {
                for (let r = startRowIndex; r < rows.length; r++) {
                    const row = rows[r] ?? [];
                    for (let i = 0; i < latest.length; i++) {
                        const vNum = row[i] ?? -120;
                        avg[i] = (avg[i] ?? 0) + vNum;
                        if (vNum > (peak[i] ?? -120)) {
                            peak[i] = vNum;
                        }
                    }
                }
                const denom = Math.max(1, rowCount);
                for (let i = 0; i < avg.length; i++) {
                    const cur = avg[i];
                    avg[i] = (typeof cur === 'number' ? cur : 0) / denom;
                }
            }

            // Secondary channel stats (if provided)
            let latest2: number[] = [];
            let avg2: number[] = [];
            let peak2: number[] = [];
            if (rows2 && rows2.length > 0) {
                latest2 = rows2.at(-1) ?? [];
                const runStats2 = secondaryStatsRef?.current ? secondaryStatsRef.current : null;
                if (runStats2 && runStats2.avg.length > 0) {
                    const end2 = Math.min(latest2.length, runStats2.avg.length);
                    avg2 = Array.from(runStats2.avg.slice(0, end2));
                    peak2 = Array.from(runStats2.peak.slice(0, end2));
                } else {
                    const rowCount2 = Math.min(rows2.length, maxRows);
                    const startRowIndex2 = Math.max(0, rows2.length - rowCount2);
                    avg2 = new Array<number>(latest2.length).fill(0);
                    peak2 = new Array<number>(latest2.length).fill(-120);
                    for (let r = startRowIndex2; r < rows2.length; r++) {
                        const row = rows2[r] ?? [];
                        for (let i = 0; i < latest2.length; i++) {
                            const vNum = row[i] ?? -120;
                            avg2[i] = (avg2[i] ?? 0) + vNum;
                            if (vNum > (peak2[i] ?? -120)) {
                                peak2[i] = vNum;
                            }
                        }
                    }
                    const denom2 = Math.max(1, rowCount2);
                    for (let i = 0; i < avg2.length; i++) {
                        const cur = avg2[i];
                        avg2[i] = (typeof cur === 'number' ? cur : 0) / denom2;
                    }
                }
            }

            if (showLatest && latest.length > 0) {
                ctx.strokeStyle = palette?.latest || RX1_PALETTE.latest;
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                const endLatest = Math.min(iEnd, latest.length - 1);
                for (let i = iStart; i <= endLatest; i += binsPerColumn) {
                    let acc = 0;
                    let cnt = 0;
                    for (let k = 0; k < binsPerColumn && i + k <= endLatest && i + k < latest.length; k++) {
                        const v = latest[i + k] ?? -120;
                        acc += v;
                        cnt++;
                    }
                    const val = cnt > 0 ? acc / cnt : -120;
                    const fBin = fStart + i * binWidth;
                    const x = Math.floor(effectiveLeftPad + fBin * currentPxPerMHz - viewLeft);
                    const y = toY(val + colorOffsetDb);
                    if (i === iStart) {
                        ctx.moveTo(x, y);
                    } else {
                        ctx.lineTo(x, y);
                    }
                }
                ctx.stroke();
            }
            // Optional: draw the provided baseline curve
            if (showBaseline && baselineLatest.length > 0) {
                ctx.strokeStyle = baselineColor || BASELINE_COLOR;
                ctx.lineWidth = 1.0;
                ctx.beginPath();
                for (let i = iStart; i <= Math.min(iEnd, baselineLatest.length - 1); i += binsPerColumn) {
                    let acc = 0;
                    let cnt = 0;
                    for (let k = 0; k < binsPerColumn && i + k <= iEnd && i + k < baselineLatest.length; k++) {
                        const v = baselineLatest[i + k] ?? -120;
                        acc += v;
                        cnt++;
                    }
                    const val = cnt > 0 ? acc / cnt : -120;
                    const fBin = fStart + i * binWidth;
                    const x = Math.floor(effectiveLeftPad + fBin * currentPxPerMHz - viewLeft);
                    const y = toY(val + colorOffsetDb);
                    if (i === iStart) {
                        ctx.moveTo(x, y);
                    } else {
                        ctx.lineTo(x, y);
                    }
                }
                ctx.stroke();
            }
            if (showAverage && latest.length > 0) {
                ctx.strokeStyle = palette?.average || RX1_PALETTE.average;
                ctx.lineWidth = 1.2;
                ctx.beginPath();
                const endAvg = Math.min(iEnd, avg.length - 1);
                for (let i = iStart; i <= endAvg; i += binsPerColumn) {
                    let acc = 0;
                    let cnt = 0;
                    for (let k = 0; k < binsPerColumn && i + k <= endAvg && i + k < avg.length; k++) {
                        const v = avg[i + k] ?? -120;
                        acc += v;
                        cnt++;
                    }
                    const val = cnt > 0 ? acc / cnt : -120;
                    const fBin = fStart + i * binWidth;
                    const x = Math.floor(effectiveLeftPad + fBin * currentPxPerMHz - viewLeft);
                    const y = toY(val + colorOffsetDb);
                    if (i === iStart) {
                        ctx.moveTo(x, y);
                    } else {
                        ctx.lineTo(x, y);
                    }
                }
                ctx.stroke();
            }

            // Draw alert threshold overlays (baseline + threshold dB)
            // Draw alert threshold overlays using percentage thresholds
            if (showAlertThresholds && baselineLatest.length > 0) {
                const pctToDb = (pct: number) => {
                    if (!Number.isFinite(pct)) {
                        return undefined;
                    }
                    // Convert percent change in power domain to dB offset: 10*log10(1 + pct/100)
                    const delta = 10 * Math.log10(1 + Math.max(-99.9, pct) / 100);
                    return Number.isFinite(delta) ? delta : undefined;
                };
                const medDb = typeof mediumThresholdPct === 'number' ? pctToDb(mediumThresholdPct) : undefined;
                const hiDb = typeof highThresholdPct === 'number' ? pctToDb(highThresholdPct) : undefined;
                if (typeof medDb === 'number') {
                    ctx.strokeStyle = thresholdColors?.medium || THRESHOLD_COLORS.medium;
                    ctx.lineWidth = 1.0;
                    ctx.beginPath();
                    const endBaseline = Math.min(iEnd, baselineLatest.length - 1);
                    for (let i = iStart; i <= endBaseline; i += binsPerColumn) {
                        let acc = 0;
                        let cnt = 0;
                        for (
                            let k = 0;
                            k < binsPerColumn && i + k <= endBaseline && i + k < baselineLatest.length;
                            k++
                        ) {
                            const v = baselineLatest[i + k] ?? -120;
                            acc += v + medDb;
                            cnt++;
                        }
                        const val = cnt > 0 ? acc / cnt : -120;
                        const fBin = fStart + i * binWidth;
                        const x = Math.floor(effectiveLeftPad + fBin * currentPxPerMHz - viewLeft);
                        const y = toY(val + colorOffsetDb);
                        if (i === iStart) {
                            ctx.moveTo(x, y);
                        } else {
                            ctx.lineTo(x, y);
                        }
                    }
                    ctx.stroke();
                }
                if (typeof hiDb === 'number') {
                    ctx.strokeStyle = thresholdColors?.high || THRESHOLD_COLORS.high;
                    ctx.lineWidth = 1.0;
                    ctx.beginPath();
                    const endBaselineHi = Math.min(iEnd, baselineLatest.length - 1);
                    for (let i = iStart; i <= endBaselineHi; i += binsPerColumn) {
                        let acc = 0;
                        let cnt = 0;
                        for (
                            let k = 0;
                            k < binsPerColumn && i + k <= endBaselineHi && i + k < baselineLatest.length;
                            k++
                        ) {
                            const v = baselineLatest[i + k] ?? -120;
                            acc += v + hiDb;
                            cnt++;
                        }
                        const val = cnt > 0 ? acc / cnt : -120;
                        const fBin = fStart + i * binWidth;
                        const x = Math.floor(effectiveLeftPad + fBin * currentPxPerMHz - viewLeft);
                        const y = toY(val + colorOffsetDb);
                        if (i === iStart) {
                            ctx.moveTo(x, y);
                        } else {
                            ctx.lineTo(x, y);
                        }
                    }
                    ctx.stroke();
                }
            }
            if (showPeak && latest.length > 0) {
                ctx.strokeStyle = palette?.peak || RX1_PALETTE.peak;
                ctx.lineWidth = 1.2;
                ctx.beginPath();
                const endPeak = Math.min(iEnd, peak.length - 1);
                for (let i = iStart; i <= endPeak; i += binsPerColumn) {
                    let maxv = -120;
                    for (let k = 0; k < binsPerColumn && i + k <= endPeak && i + k < peak.length; k++) {
                        const v = peak[i + k] ?? -120;
                        if (v > maxv) {
                            maxv = v;
                        }
                    }
                    const val = maxv;
                    const fBin = fStart + i * binWidth;
                    const x = Math.floor(effectiveLeftPad + fBin * currentPxPerMHz - viewLeft);
                    const y = toY(val + colorOffsetDb);
                    if (i === iStart) {
                        ctx.moveTo(x, y);
                    } else {
                        ctx.lineTo(x, y);
                    }
                }
                ctx.stroke();
            }

            // Overlay secondary channel traces
            if (rows2 && latest2.length > 0) {
                if (showLatest) {
                    ctx.strokeStyle = RX2_PALETTE.latest;
                    ctx.lineWidth = 1.2;
                    ctx.beginPath();
                    for (let i = iStart; i <= Math.min(iEnd, latest2.length - 1); i += binsPerColumn) {
                        let acc = 0;
                        let cnt = 0;
                        for (let k = 0; k < binsPerColumn && i + k <= iEnd && i + k < latest2.length; k++) {
                            const v = latest2[i + k] ?? -120;
                            acc += v;
                            cnt++;
                        }
                        const val = cnt > 0 ? acc / cnt : -120;
                        const fBin = fStart + i * binWidth;
                        const x = Math.floor(effectiveLeftPad + fBin * currentPxPerMHz - viewLeft);
                        const y = toY(val + colorOffsetDb);
                        if (i === iStart) {
                            ctx.moveTo(x, y);
                        } else {
                            ctx.lineTo(x, y);
                        }
                    }
                    ctx.stroke();
                }
                if (showAverage && avg2.length > 0) {
                    ctx.strokeStyle = RX2_PALETTE.average;
                    ctx.lineWidth = 1.0;
                    ctx.beginPath();
                    for (let i = iStart; i <= Math.min(iEnd, avg2.length - 1); i += binsPerColumn) {
                        let acc = 0;
                        let cnt = 0;
                        for (let k = 0; k < binsPerColumn && i + k <= iEnd && i + k < avg2.length; k++) {
                            const v = avg2[i + k] ?? -120;
                            acc += v;
                            cnt++;
                        }
                        const val = cnt > 0 ? acc / cnt : -120;
                        const fBin = fStart + i * binWidth;
                        const x = Math.floor(effectiveLeftPad + fBin * currentPxPerMHz - viewLeft);
                        const y = toY(val + colorOffsetDb);
                        if (i === iStart) {
                            ctx.moveTo(x, y);
                        } else {
                            ctx.lineTo(x, y);
                        }
                    }
                    ctx.stroke();
                }
                if (showPeak && peak2.length > 0) {
                    ctx.strokeStyle = RX2_PALETTE.peak;
                    ctx.lineWidth = 1.0;
                    ctx.beginPath();
                    for (let i = iStart; i <= Math.min(iEnd, peak2.length - 1); i += binsPerColumn) {
                        let maxv = -120;
                        for (let k = 0; k < binsPerColumn && i + k <= iEnd && i + k < peak2.length; k++) {
                            const v = peak2[i + k] ?? -120;
                            if (v > maxv) {
                                maxv = v;
                            }
                        }
                        const val = maxv;
                        const fBin = fStart + i * binWidth;
                        const x = Math.floor(effectiveLeftPad + fBin * currentPxPerMHz - viewLeft);
                        const y = toY(val + colorOffsetDb);
                        if (i === iStart) {
                            ctx.moveTo(x, y);
                        } else {
                            ctx.lineTo(x, y);
                        }
                    }
                    ctx.stroke();
                }
            }

            // Crosshair and data marker
            if (cursorRef?.current && typeof cursorRef.current.xContent === 'number') {
                const cursorFrequency = getSweepCursorFrequency({
                    contentX: cursorRef.current.xContent,
                    drawConfig: drawConfigRef?.current ?? null,
                    leftPadPx,
                    pxPerMHz: currentPxPerMHz,
                    totalMHz,
                });
                const viewLeftLocal = container.scrollLeft;
                const xCanvas = Math.floor(cursorFrequency.contentX - viewLeftLocal);
                const markerBinIndex = cursorFrequency.binIndex;
                const preferredTraceMode = getPreferredSweepTraceMode({
                    hasAverage:
                        getMaximumFiniteValue([
                            getSweepSampleAtBin(avg, markerBinIndex),
                            getSweepSampleAtBin(avg2, markerBinIndex),
                        ]) !== null,
                    hasLatest:
                        getMaximumFiniteValue([
                            getSweepSampleAtBin(latest, markerBinIndex),
                            getSweepSampleAtBin(latest2, markerBinIndex),
                        ]) !== null,
                    hasPeak:
                        getMaximumFiniteValue([
                            getSweepSampleAtBin(peak, markerBinIndex),
                            getSweepSampleAtBin(peak2, markerBinIndex),
                        ]) !== null,
                    showAverage,
                    showLatest,
                    showPeak,
                });
                const markerValue = getSweepTraceValueAtBin({
                    binIndex: markerBinIndex,
                    mode: preferredTraceMode,
                    primary: {
                        average: avg,
                        baseline: baselineLatest,
                        latest,
                        peak,
                    },
                    secondary: {
                        average: avg2,
                        latest: latest2,
                        peak: peak2,
                    },
                });

                if (typeof markerValue === 'number') {
                    const y = toY(markerValue + colorOffsetDb);
                    // vertical line
                    ctx.save();
                    ctx.strokeStyle = '#e0d200';
                    ctx.globalAlpha = 0.6;
                    ctx.beginPath();
                    ctx.moveTo(xCanvas + 0.5, 0);
                    ctx.lineTo(xCanvas + 0.5, cssHeight);
                    ctx.stroke();
                    // marker circle
                    ctx.fillStyle = '#F9FAFC';
                    ctx.strokeStyle = '#1f1f1f';
                    ctx.globalAlpha = 1;
                    ctx.beginPath();
                    ctx.arc(xCanvas, y, 5, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.lineWidth = 1.5;
                    ctx.stroke();
                    ctx.restore();
                }
            }

            // Alert markers overlay (cap drawing and freeze thereafter, see resolveAlertMarkersToDraw)
            for (const m of resolveAlertMarkersToDraw()) {
                const x = Math.floor(effectiveLeftPad + m.freqMHz * currentPxPerMHz - viewLeft);
                const alertBinIndex = Math.round((m.freqMHz - fStart) / Math.max(1e-6, binWidth));
                const clampedAlertBinIndex = Math.max(0, Math.min(peak.length - 1, alertBinIndex));
                const peakValueAtAlertBin = peak[clampedAlertBinIndex];
                const markerDb = Number.isFinite(peakValueAtAlertBin) ? peakValueAtAlertBin : m.db;
                const y = toY(markerDb! + colorOffsetDb);
                // Only draw if within canvas bounds
                if (x < 0 || x > viewportCssW || y < 0 || y > cssHeight) {
                    continue;
                }
                ctx.save();
                // severity colors (medium=orange, high=red). Low falls back to medium
                let color = THRESHOLD_COLORS.medium;
                if (m.severity === 'high') {
                    color = THRESHOLD_COLORS.high;
                }
                // vertical line from top down to the detected peak (do not pass through)
                ctx.globalAlpha = 0.95;
                ctx.strokeStyle = color;
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x, y);
                ctx.stroke();
                // downward pointing triangle at the marker y
                const triW = 8;
                const triH = 8;
                ctx.globalAlpha = 1;
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.moveTo(x, y); // tip
                ctx.lineTo(x - triW / 2, y - triH);
                ctx.lineTo(x + triW / 2, y - triH);
                ctx.closePath();
                ctx.fill();
                ctx.restore();
            }
        };

        const drawWaterfall = () => {
            const currentPxPerMHz = pxPerMHzRef.current;
            const canvas = waterfallCanvasRef.current;
            const container = containerRef.current;
            if (!canvas || !container) {
                return;
            }
            const viewportCssW = container.clientWidth;
            const rowHeight = rowHeightPx;
            const rows = sweepRowsRef?.current ? sweepRowsRef.current : [];
            const rows2 = secondaryRowsRef ? secondaryRowsRef.current : undefined;
            if (rows.length < 0) {
                // noop
            }
            if (rows.length > 0) {
                // nothing special here - rendering below handles content
            }
            if (rows.length > 0) {
                /* continue */
            }
            if (rows.length < 0) {
                /* continue */
            }
            // compute viewport
            if (rows.length === 0) {
                // clear canvas
            }
            const viewportHeightBase = Math.max(0, height - SPECTRUM_WATERFALL_AXIS_HEIGHT_PX - spectrumCssH);
            const viewportHeight =
                typeof waterfallCssHeightOverride === 'number' ? waterfallCssHeightOverride : viewportHeightBase;
            const rowsFit = Math.max(1, Math.floor(viewportHeight / rowHeight));
            const rowCount = Math.min(rows.length, rowsFit);
            const cssHeight = fillWaterfallViewport
                ? Math.max(0, Math.floor(viewportHeight))
                : Math.max(0, rowCount * rowHeight);
            // set sizes (only width; height is fixed to avoid scrollbars)
            const effectiveContentWidth = getSweepContentWidth({
                leftPadPx,
                pxPerMHz: currentPxPerMHz,
                rightPadPx,
                totalMHz,
            });
            const horizontalOverflowPx = Math.max(0, effectiveContentWidth - viewportCssW);

            container.style.overflowX = horizontalOverflowPx <= 4 ? 'hidden' : 'auto';

            if (horizontalOverflowPx <= 4 && container.scrollLeft !== 0) {
                container.scrollLeft = 0;
            }

            if (contentBoxRef.current) {
                contentBoxRef.current.style.width = String(effectiveContentWidth) + 'px';
            }
            canvas.style.width = viewportCssW.toString() + 'px';
            canvas.style.height = cssHeight.toString() + 'px';
            const viewLeft = container.scrollLeft;
            canvas.style.left = viewLeft.toString() + 'px';
            const maxDevicePixels = 16_384;
            const desiredW = Math.floor(viewportCssW * devicePixelRatio);
            const desiredH = Math.floor(cssHeight * devicePixelRatio);
            canvas.width = Math.min(desiredW, maxDevicePixels);
            canvas.height = Math.min(desiredH, maxDevicePixels);
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return;
            }
            const xScaleWf = canvas.width / viewportCssW;
            const yScaleWf = canvas.height / cssHeight || 1;
            ctx.setTransform(xScaleWf, 0, 0, yScaleWf, 0, 0);
            canvasMetricsRef.current = {
                axis: canvasMetricsRef.current.axis,
                spectrum: canvasMetricsRef.current.spectrum,
                waterfall: { cssH: cssHeight, cssW: viewportCssW, devH: canvas.height, devW: canvas.width },
            };
            ctx.fillStyle = '#061026';
            ctx.fillRect(0, 0, viewportCssW, cssHeight);
            const fStart =
                drawConfigRef?.current && typeof drawConfigRef.current.freqStartMHz === 'number'
                    ? drawConfigRef.current.freqStartMHz
                    : 0;
            const binWidth =
                drawConfigRef?.current && typeof drawConfigRef.current.binWidthMHz === 'number'
                    ? drawConfigRef.current.binWidthMHz
                    : 1;
            const viewRight = viewLeft + viewportCssW;
            const effectiveLeftPad = leftPadPx + labelAreaPx;
            const fVisibleStart = Math.max(0, (viewLeft - effectiveLeftPad) / Math.max(1e-6, currentPxPerMHz));
            const fVisibleEnd = Math.min(totalMHz, (viewRight - effectiveLeftPad) / Math.max(1e-6, currentPxPerMHz));
            const xScaleWfLocal = canvas.width / viewportCssW;
            const devicePxPerBinBase = binWidth * currentPxPerMHz * xScaleWfLocal * devicePixelRatio;
            const binsPerColumnBase =
                devicePxPerBinBase < 1 ? Math.max(1, Math.floor(1 / Math.max(1e-6, devicePxPerBinBase))) : 1;
            const iStart = Math.max(0, Math.floor((fVisibleStart - fStart) / Math.max(1e-6, binWidth)));
            const iEndPrimary = rows.length > 0 ? Math.max(0, (rows.at(-1) ?? []).length - 1) : -1;
            const iEndSecondary = rows2 && rows2.length > 0 ? Math.max(0, (rows2.at(-1) ?? []).length - 1) : -1;
            const maxVisibleBinIndex =
                iEndPrimary >= 0 || iEndSecondary >= 0
                    ? Math.max(
                          iStart,
                          Math.max(iEndPrimary, iEndSecondary),
                          Math.ceil((fVisibleEnd - fStart) / Math.max(1e-6, binWidth)),
                      )
                    : -1;

            const primaryRowCountForScale = Math.min(rowCount, rows.length);
            const primaryScaleRows =
                primaryRowCountForScale > 0 ? rows.slice(rows.length - primaryRowCountForScale) : [];
            const secondaryScaleRows =
                rows2 && rows2.length > 0 ? rows2.slice(rows2.length - Math.min(rowCount, rows2.length)) : [];
            const rawAdaptiveScale = resolveAdaptiveWaterfallScale({
                colorOffsetDb,
                columnStep: binsPerColumnBase,
                maxBinIndex: maxVisibleBinIndex,
                rows: [...primaryScaleRows, ...secondaryScaleRows],
            });
            const nowMs = Date.now();
            const scaleUpdate = smoothAdaptiveWaterfallScale({
                lastUpdatedAtMs: lastWaterfallScaleUpdateAtMsRef.current,
                nowMs,
                previousScale: smoothedWaterfallScaleRef.current,
                rawScale: rawAdaptiveScale,
            });
            const adaptiveScale = scaleUpdate.scale;

            if (scaleUpdate.didUpdate || smoothedWaterfallScaleRef.current === null) {
                smoothedWaterfallScaleRef.current = adaptiveScale;
                lastWaterfallScaleUpdateAtMsRef.current = nowMs;
            }

            const drawRows = (rowsIn: SweepRows, alpha: number) => {
                if (rowsIn.length === 0) {
                    return;
                }
                const rowCountLocal = Math.min(rowCount, rowsIn.length);
                ctx.save();
                ctx.globalAlpha = alpha;
                // Newest line at the top so time falls downward like a conventional RF waterfall.
                for (let r = 0; r < rowCountLocal; r++) {
                    const sourceRowIndex = rowsIn.length - 1 - r;
                    const row = rowsIn[sourceRowIndex] ?? [];
                    const y = r * rowHeight;
                    const iEnd = Math.min(row.length - 1, Math.ceil((fVisibleEnd - fStart) / Math.max(1e-6, binWidth)));
                    for (let i = iStart; i <= iEnd; i += binsPerColumnBase) {
                        let acc = 0;
                        let cnt = 0;
                        for (let k = 0; k < binsPerColumnBase && i + k <= iEnd; k++) {
                            const v = row[i + k] ?? -120;
                            acc += v;
                            cnt++;
                        }
                        const val = cnt > 0 ? acc / cnt : -120;
                        const fBin = fStart + i * binWidth;
                        // Center the cell on the bin's own pixel position (matching drawSpectrum's line
                        // vertex and the alert-dot x), instead of anchoring the left edge there and
                        // extending one bin-width right — that made the last bin's box overshoot past the
                        // data range and get clamped/crushed against the viewport edge below.
                        const centerX = leftPadPx + labelAreaPx + fBin * currentPxPerMHz - viewLeft;
                        const w = Math.max(1, Math.ceil(binWidth * currentPxPerMHz * binsPerColumnBase));
                        const x = Math.floor(centerX - w / 2);
                        const xClamped = Math.max(0, Math.min(viewportCssW - 1, x));
                        const wClamped = Math.max(1, Math.min(w, viewportCssW - xClamped));
                        ctx.fillStyle = getAdaptiveWaterfallColor(val + colorOffsetDb, adaptiveScale);
                        ctx.fillRect(xClamped, y, wClamped, rowHeight);
                    }
                }
                ctx.restore();
            };

            // Primary and secondary overlays
            drawRows(rows, 1.0);
            if (rows2 && rows2.length > 0) {
                drawRows(rows2, 0.7);
            }

            // Alert markers overlay: anchor each marker to the data row it fired on (rowIndex) so it
            // scrolls with the waterfall like the Plan Report images do, fading out and disappearing
            // once that row scrolls past the visible viewport instead of sticking at the top edge.
            for (const m of resolveAlertMarkersToDraw()) {
                if (typeof m.rowIndex !== 'number') {
                    continue;
                }
                const rowsAgo = rows.length - 1 - m.rowIndex;
                if (rowsAgo < 0 || rowsAgo >= rowCount) {
                    continue;
                }
                const x = Math.floor(effectiveLeftPad + m.freqMHz * currentPxPerMHz - viewLeft);
                if (x < 0 || x > viewportCssW) {
                    continue;
                }
                const y = rowsAgo * rowHeight + rowHeight / 2;
                const color = m.severity === 'high' ? THRESHOLD_COLORS.high : THRESHOLD_COLORS.medium;
                const radius = m.severity === 'high' ? 5 : 4;
                const fadeAlpha = 1 - rowsAgo / rowCount;
                ctx.save();
                // Keep a higher alpha floor than the raw fade so the dot never blends into a
                // similarly-colored waterfall cell, and add an outer dark ring + drop shadow so
                // it reads clearly regardless of what's underneath (light or dark waterfall bins).
                ctx.globalAlpha = Math.max(0.55, fadeAlpha);
                ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
                ctx.shadowBlur = 3;
                ctx.fillStyle = 'rgba(0, 0, 0, 0.9)';
                ctx.beginPath();
                ctx.arc(x, y, radius + 1.5, 0, Math.PI * 2);
                ctx.fill();
                ctx.shadowBlur = 0;
                ctx.fillStyle = color;
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                ctx.arc(x, y, radius, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();
                ctx.restore();
            }

            // Snapshot capture hook
            if (captureRecentTile) {
                const prevLen = lastRowsLenRef.current;
                const delta = rows.length - prevLen;
                if (delta > 0) {
                    // Accumulate only when new rows are appended
                    accumPxSinceCaptureRef.current = accumPxSinceCaptureRef.current + delta * rowHeight;
                    lastRowsLenRef.current = rows.length;
                } else if (delta < 0) {
                    // Buffer trimmed; update baseline without decreasing accumulation
                    lastRowsLenRef.current = rows.length;
                } else if (rows.length === 0) {
                    // Cleared buffers: reset accumulation
                    accumPxSinceCaptureRef.current = 0;
                    lastRowsLenRef.current = 0;
                }
                const threshold = typeof snapshotTileHeightPx === 'number' ? snapshotTileHeightPx : 200;
                if (accumPxSinceCaptureRef.current >= threshold) {
                    // fire and reset overshoot
                    try {
                        void captureRecentTile();
                    } catch {
                        // ignore capture errors here; UI will continue
                    }
                    accumPxSinceCaptureRef.current = Math.max(0, accumPxSinceCaptureRef.current - threshold);
                }
            }
        };

        // measure + event wiring
        const measure = () => {
            const currentPxPerMHz = pxPerMHzRef.current;
            drawAxis();
            drawSpectrum();
            drawWaterfall();
            const el = contentBoxRef.current;
            if (el) {
                el.style.width =
                    String(
                        Math.max(1024, Math.floor(currentPxPerMHz * totalMHz) + leftPadPx + labelAreaPx + rightPadPx),
                    ) + 'px';
            }
        };

        drawAxisRef.current = drawAxis;
        drawSpectrumRef.current = drawSpectrum;
        drawWaterfallRef.current = drawWaterfall;
        measureRef.current = measure;

        measure();
        const onResize = () => measure();
        window.addEventListener('resize', onResize);
        const el = containerRef.current;
        let scrollRaf = 0;
        const onScroll = () => {
            if (scrollRaf) {
                cancelAnimationFrame(scrollRaf);
            }
            scrollRaf = requestAnimationFrame(() => {
                drawAxis();
                drawSpectrum();
                drawWaterfall();
            });
        };
        if (el) {
            el.addEventListener('scroll', onScroll);
        }

        // Observe container size changes (layout toggles, flex direction changes)
        let ro: ResizeObserver | null = null;
        try {
            if (el && typeof ResizeObserver !== 'undefined') {
                ro = new ResizeObserver(() => {
                    measure();
                });
                ro.observe(el);
            }
        } catch {
            // Ignore ResizeObserver errors in unsupported environments
            ro = null;
        }

        // RAF loop: redraw when frameCounterRef changes
        let rafId = 0;
        let lastFrame = frameCounterRef.current;
        const loop = () => {
            rafId = requestAnimationFrame(loop);
            const frame = frameCounterRef.current;
            if (frame !== lastFrame) {
                lastFrame = frame;
                drawSpectrum();
                drawWaterfall();
            }
        };
        rafId = requestAnimationFrame(loop);

        return () => {
            drawAxisRef.current = null;
            drawSpectrumRef.current = null;
            drawWaterfallRef.current = null;
            measureRef.current = null;
            window.removeEventListener('resize', onResize);
            if (el) {
                el.removeEventListener('scroll', onScroll);
            }
            if (scrollRaf) {
                cancelAnimationFrame(scrollRaf);
            }
            if (rafId) {
                cancelAnimationFrame(rafId);
            }
            if (ro) {
                try {
                    ro.disconnect();
                } catch {
                    // ignore
                }
            }
        };
    }, [
        containerRef,
        contentBoxRef,
        axisCanvasRef,
        spectrumCanvasRef,
        waterfallCanvasRef,
        sweepRowsRef,
        secondaryRowsRef,
        baselineRowsRef,
        alertMarkersRef,
        layoutKey,
        canvasMetricsRef,
        frameCounterRef,
        devicePixelRatio,
        leftPadPx,
        totalMHz,
        spectrumCssH,
        height,
        rowHeightPx,
        colorOffsetDb,
        maxRows,
        showLatest,
        showAverage,
        showPeak,
        captureRecentTile,
        showAlertThresholds,
        mediumThresholdPct,
        highThresholdPct,
        thresholdColors,
        showBaseline,
        baselineColor,
        baselineRef,
        drawConfigRef,
        statsRef,
        cursorRef,
        baselineChannel,
        secondaryStatsRef,
        palette?.latest,
        palette?.average,
        palette?.peak,
        waterfallCssHeightOverride,
        rightPadPx,
        snapshotTileHeightPx,
        fillWaterfallViewport,
    ]);

    useLayoutEffect(() => {
        pxPerMHzRef.current = pxPerMHz;

        measureRef.current?.();
    }, [pxPerMHz]);
}
